import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import FinanciersList from '../components/FinanciersList';
import { ForumFilmQuery } from '../gql/api';

export const joinTranslations = (arr: { translation?: string }[] | null) =>
    arr?.map(({ translation }) => translation).join(', ');

const sortBySortOrder = (a: { sortOrder?: number }, b: { sortOrder?: number }) => a.sortOrder - b.sortOrder;

export const getProjectInformationLines = (film: ForumFilmQuery['film']) =>
    [
        film?.edition?.name && {
            key: 'selected-for',
            description: <FormattedMessage defaultMessage="Selected for" />,
            value: film.edition.name,
        },
        film?.countriesOfProduction && {
            key: 'countries-of-production',
            description: <FormattedMessage defaultMessage="Countries of production" />,
            value: joinTranslations(film.countriesOfProduction),
        },
        film?.countriesOfCoproduction && {
            key: 'countries-of-coproduction',
            description: <FormattedMessage defaultMessage="Countries of co‑production" />,
            value: joinTranslations(film.countriesOfCoproduction),
        },
        film?.productionStatus && {
            key: 'production-status',
            description: <FormattedMessage defaultMessage="Production status" />,
            value: joinTranslations(film.productionStatus),
        },
    ]
        .filter(Boolean)
        .filter(line => !!line.value);

export const getProjectDetailsLines = (film?: ForumFilmQuery['film']) =>
    [
        film?.requestType && {
            key: 'requestType',
            description: <FormattedMessage defaultMessage="IDFA Forum Presentation category" />,
            value: joinTranslations(film.requestType.sort(sortBySortOrder)),
        },
        film?.startProduction && {
            key: 'startProduction',
            description: <FormattedMessage defaultMessage="Production start" />,
            value: film.startProduction,
        },
        film?.monthOfProduction &&
            film?.yearOfProduction && {
                key: 'productionEnd',
                description: <FormattedMessage defaultMessage="Production end" />,
                value: film.monthOfProduction && film.yearOfProduction && (
                    <FormattedDate
                        value={new Date(film.yearOfProduction, film.monthOfProduction - 1)}
                        year="numeric"
                        month="long"
                    />
                ),
            },
        film?.startRelease && {
            key: 'startRelease',
            description: <FormattedMessage defaultMessage="Expected release" />,
            value: film.startRelease,
        },
        film?.lengths && {
            key: 'lengths',
            description: <FormattedMessage defaultMessage="Length" />,
            value: film.lengths,
        },
        film?.standAloneVersions && {
            key: 'standAloneVersions',
            description: <FormattedMessage defaultMessage="Stand-alone versions" />,
            value: joinTranslations(film.standAloneVersions?.sort(sortBySortOrder)),
        },
        film?.typeOfNewMediaProject && {
            key: 'typeOfNewMediaProject',
            description: <FormattedMessage defaultMessage="Type of New Media Project" />,
            value: joinTranslations(film.typeOfNewMediaProject?.sort(sortBySortOrder)),
        },
        film?.totalBudget && {
            key: 'totalBudget',
            description: <FormattedMessage defaultMessage="Production budget" />,
            value: film.totalBudget && (
                <FormattedNumber value={film.totalBudget}>
                    {formattedNumber => <>€ {formattedNumber}</>}
                </FormattedNumber>
            ),
        },
        film?.financingInPlace && {
            key: 'financingInPlace',
            description: <FormattedMessage defaultMessage="Financing in place" />,
            value: film.financingInPlace && (
                <FormattedNumber value={film.financingInPlace}>
                    {formattedNumber => <>€ {formattedNumber}</>}
                </FormattedNumber>
            ),
        },
        film?.requestedFinancing && {
            key: 'requestedFinancing',
            description: <FormattedMessage defaultMessage="Still required" />,
            value: film.requestedFinancing && (
                <FormattedNumber value={film.requestedFinancing}>
                    {formattedNumber => <>€ {formattedNumber}</>}
                </FormattedNumber>
            ),
        },
        film?.financiers && {
            key: 'financiers',
            description: <FormattedMessage defaultMessage="Financiers" />,
            value: film.financiers?.length > 0 && <FinanciersList financiers={film.financiers} />,
        },
        film?.forumOpenTalent && {
            key: 'forumOpenTalent',
            description: <FormattedMessage defaultMessage="Open Talent for co‑production" />,
            value: film.forumOpenTalent?.translation,
        },
        film?.forumCoprodPotential && {
            key: 'forumCoprodPotential',
            description: <FormattedMessage defaultMessage="co‑production potential in storyline" />,
            value: film.forumCoprodPotential?.translation,
        },
        film?.interestedInFollowingProducers && {
            key: 'interestedInFollowingProducers',
            description: <FormattedMessage defaultMessage="Looking for" />,
            value: film.interestedInFollowingProducers,
        },
    ]
        .filter(Boolean)
        .filter(detail => !!detail.value);
